import React, { useRef, useState, useEffect } from 'react';

function formatDuration(seconds) {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function VideoPlayer({ video, onClose, playlist, currentIndex, onNext, onPrev }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoPlayBlocked, setAutoPlayBlocked] = useState(false);

  const hasNext = playlist && currentIndex !== null && currentIndex < playlist.length - 1;
  const nextVideo = hasNext ? playlist[currentIndex + 1] : null;

  useEffect(() => {
    const videoElement = videoRef.current;
    setAutoPlayBlocked(false);
    if (videoElement) {
      videoElement.load();
      videoElement.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.log('Auto-play prevented:', err);
        setAutoPlayBlocked(true);
      });
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'n') {
        if (onNext) onNext();
      } else if (e.key === 'ArrowLeft' || e.key === 'p') {
        // handled by onPrev prop
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [video]);

  const handleEnded = () => {
    setIsPlaying(false);
    if (onNext) {
      onNext();
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = vol;
      setVolume(vol);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`/api/downloads/download/${video.id}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = video.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
      alert('Failed to download file: ' + err.message);
    }
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <button className="close-btn" onClick={onClose}>×</button>
      
      <div className="video-player-container">
        <video
          ref={videoRef}
          src={`/api/downloads/stream/${video.id}`}
          poster={video.thumbnail_url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onClick={togglePlay}
          playsInline
          controls={false}
          style={{ width: '100%', height: 'auto', maxHeight: '70vh', background: '#000' }}
        >
          Your browser does not support the video tag.
        </video>

        <div className="custom-controls" style={{
          padding: '16px 20px',
          background: '#1a1a1a',
          borderTop: '1px solid #333'
        }}>
          {/* Progress bar */}
          <div style={{ marginBottom: '12px' }}>
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              style={{
                width: '100%',
                height: '4px',
                appearance: 'none',
                background: `linear-gradient(to right, #ff0000 ${progressPercent}%, #333 ${progressPercent}%)`,
                borderRadius: '2px',
                cursor: 'pointer'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '12px', color: '#aaa' }}>
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(duration)}</span>
            </div>
          </div>

          {/* Control buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {onPrev && (
                <button
                  onClick={onPrev}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: currentIndex > 0 ? 1 : 0.3
                  }}
                  disabled={currentIndex <= 0}
                >
                  ⏮
                </button>
              )}
              <button
                onClick={togglePlay}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              {onNext && (
                <button
                  onClick={onNext}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#fff',
                    fontSize: '24px',
                    cursor: 'pointer',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: hasNext ? 1 : 0.3
                  }}
                  disabled={!hasNext}
                >
                  ⏭
                </button>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#aaa', fontSize: '18px' }}>🔊</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={volume}
                  onChange={handleVolumeChange}
                  style={{
                    width: '80px',
                    height: '4px',
                    appearance: 'none',
                    background: `linear-gradient(to right, #fff ${volume * 100}%, #333 ${volume * 100}%)`,
                    borderRadius: '2px',
                    cursor: 'pointer'
                  }}
                />
              </div>

              <div style={{ marginLeft: '20px' }}>
                <h3 style={{ fontSize: '14px', margin: 0, marginBottom: '4px' }}>
                  {playlist && currentIndex !== null ? `Now Playing (${currentIndex + 1}/${playlist.length})` : ''} {video.title}
                </h3>
                <span style={{ fontSize: '12px', color: '#888' }}>
                  {video.quality} • {video.is_compressed ? 'Compressed' : 'Original'}
                </span>
                {autoPlayBlocked && (
                  <span style={{ fontSize: '11px', color: '#f9a825', display: 'block', marginTop: '2px' }}>
                    ⚠ Auto-play blocked — click to play next
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={handleDownload}
                style={{
                  padding: '8px 16px',
                  background: '#272727',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                ⬇ Download
              </button>
              <button 
                onClick={toggleFullscreen}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  fontSize: '20px',
                  cursor: 'pointer',
                  width: '40px',
                  height: '40px'
                }}
              >
                ⛶
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;